export enum WsMessageType {
  ALARM = 'ALARM',
  WARN = 'WARN',
  ALERT = 'ALERT',
  DEVICE = 'DEVICE',
  AIS = 'AIS',
  CCTV = 'CCTV',
  WIDGET = 'WIDGET',
  NMS = 'NMS',
  CONNECT = 'CONNECT',
  DISCONNECT = 'DISCONNECT',
}

export enum WsEventType {
  SACON_PRODUCT_PROCESSED = 'SACON_PRODUCT_PROCESSED',
  SACON_PRODUCT_INFO = 'SACON_PRODUCT_INFO',
  SACON_PRODUCT_ALERT = 'SACON_PRODUCT_ALERT',
  SACON_PRODUCT_WARNING = 'SACON_PRODUCT_WARNING',
  SACON_PRODUCT_ERROR = 'SACON_PRODUCT_ERROR',
  WS_CONNECTED = 'WS_CONNECTED',
  WS_DISCONNECTED = 'WS_DISCONNECTED',
  WARNING = 'WARNING',
  ALERT = 'ALERT',
  WORKER_COUNT = 'WORKER_COUNT',
  DEVICE_COUNT = 'DEVICE_COUNT',
  ISSUED_ASSET_STATUS = 'ISSUED_ASSET_STATUS',
  ISSUED_ASSET_RETURN_DUE = 'ISSUED_ASSET_RETURN_DUE',
  ISSUED_ASSET_RETURN_OVERDUE = 'ISSUED_ASSET_RETURN_OVERDUE',
  SAFETY_GUARD_MISSING_WORKER_ALERT = 'SAFETY_GUARD_MISSING_WORKER_ALERT',
  CONSTRUCTION_EQUIPMENT_RENTAL_COUNT = 'CONSTRUCTION_EQUIPMENT_RENTAL_COUNT',
  CONSTRUCTION_EQUIPMENT_RENTAL_MISMATCH_ALERT = 'CONSTRUCTION_EQUIPMENT_RENTAL_MISMATCH_ALERT',
  MEDIA_GATEWAY_DEGRADED = 'MEDIA_GATEWAY_DEGRADED',
  DEVICE_GEOFENCE_ESCAPE = 'DEVICE_GEOFENCE_ESCAPE',
  DEVICE_GEOFENCE_RETURNED = 'DEVICE_GEOFENCE_RETURNED',
  ALARM_COUNT_CHANGED = 'ALARM_COUNT_CHANGED',
  DAILY_OPERATION_SNAPSHOT = 'DAILY_OPERATION_SNAPSHOT',
  DAILY_OPERATION_STATE_CHANGED = 'DAILY_OPERATION_STATE_CHANGED',
}

export const ALARM_CHANNEL_MESSAGE_TYPES = [WsMessageType.ALARM, WsMessageType.WARN, WsMessageType.ALERT] as const;

export type AlarmChannelMessageType = (typeof ALARM_CHANNEL_MESSAGE_TYPES)[number];

/** 알림 SSE 채널에서 처리할 수 있는 알람성 메시지 타입인지 확인합니다. */
export function isAlarmChannelMessageType(type: unknown): type is AlarmChannelMessageType {
  return typeof type === 'string' && ALARM_CHANNEL_MESSAGE_TYPES.includes(type as AlarmChannelMessageType);
}

export type WsAisBody = Record<string, unknown>;
export type WsMobileGpsBody = Record<string, unknown>;
export type WsSaconProductBody = Record<string, unknown>;
export type WsAlarmBody = Record<string, unknown>;

export interface WsWorkerCountBody {
  field_uid: string;
  total: number;
  app_users: {
    total: number;
    workers: Array<Record<string, unknown>>;
  };
  iot_users: {
    total: number;
    ships: Array<Record<string, unknown>>;
  };
}

export interface WsDeviceCountBody {
  field_uid: string;
  count_basis:
    | 'MAP_MARKER_GROUPED_BY_TYPE'
    | 'DELIVERED_PRODUCT_BY_NAME'
    | 'INSTALLED_PRODUCT_BY_NAME'
    | 'REGISTERED_PRODUCT_BY_NAME'
    | 'REGISTERED_DEVICE_GROUPED_BY_PRODUCT_CATEGORY';
  total: number;
  active_total: number;
  counts: Array<{
    device_type: string;
    label: string;
    count: number;
    active_count: number;
  }>;
}

export interface WsIssuedAssetStatusBody {
  field_uid: string;
  issued_asset_uid: string;
  device_uid: string;
  worker_uid?: string | null;
  construction_equipment_uid?: string | null;
  equipment_code?: string | null;
  equipment_name?: string | null;
  recipient_type?: string;
  status: string;
  current_status: string;
  returned_at?: string | null;
  reason?: string;
}

export interface WsConstructionEquipmentRentalCountBody {
  field_uid: string;
  operation_date: string;
  expected_count: number;
  realtime_count: number;
  mismatch_count: number;
  auto_detected_count?: number;
  construction_equipment_expected_count?: number;
  construction_equipment_realtime_count?: number;
  construction_equipment_mismatch_count?: number;
  construction_equipment_auto_detected_count?: number;
  worker_expected_count?: number;
  worker_realtime_count?: number;
  worker_mismatch_count?: number;
  worker_auto_detected_count?: number;
  cctv_expected_count?: number;
  cctv_realtime_count?: number;
  cctv_mismatch_count?: number;
  cctv_auto_detected_count?: number;
  iot_device_expected_count?: number;
  iot_device_realtime_count?: number;
  iot_device_mismatch_count?: number;
  target_counts?: Array<{
    target_type: string;
    total: number;
    normal_operation: number;
    selected_not_detected: number;
    auto_detected: number;
  }>;
  validation_scope?: 'CONSTRUCTION_EQUIPMENT_ONLY' | 'ALL_ISSUED_ASSET_DEVICES' | 'DAILY_OPERATION_STATE';
  enabled: boolean;
  checked_at: string;
  items: Array<{
    target_type: string;
    target_uid: string;
    target_name: string;
    company_name?: string | null;
    device_display_number?: string | null;
    selected: boolean;
    realtime_state: 'ON' | 'OFF' | 'UNKNOWN';
    operation_status: 'NORMAL_OPERATION' | 'SELECTED_NOT_DETECTED' | 'AUTO_DETECTED' | 'NOT_DEPLOYED';
    last_detected_at?: string | null;
    issued_asset_uid?: string | null;
    device_uid?: string | null;
    device_name?: string | null;
    display_name?: string | null;
    construction_equipment_uid?: string | null;
    worker_uid?: string | null;
    worker_name?: string | null;
    equipment_code?: string | null;
    equipment_name?: string | null;
    rental_device_type?: string | null;
    rental_device_type_text?: string | null;
    is_realtime_active: boolean;
    last_seen_at?: string | null;
  }>;
}

export interface WsAlarmCountChangedBody {
  field_uid: string;
  reason: string;
  updated_count: number;
  closed_at?: string | null;
}

export type WsMessageBody =
  | WsAisBody
  | WsMobileGpsBody
  | WsSaconProductBody
  | WsAlarmBody
  | WsWorkerCountBody
  | WsDeviceCountBody
  | WsIssuedAssetStatusBody
  | WsConstructionEquipmentRentalCountBody
  | WsAlarmCountChangedBody
  | Record<string, unknown>;

export interface WsBaseMessage<TBody = WsMessageBody> {
  type: WsMessageType;
  event: WsEventType;
  key: string;
  sent_at: string;
  topic: string;
  /** 백엔드에서 현장 구분자로 제공하는 루트 필드 UID (ALARM 포함) */
  field_uid?: string;
  body: TBody;
}

export interface WsAisMessage extends WsBaseMessage<WsAisBody> {
  type: WsMessageType.AIS;
}

export interface WsMobileGpsMessage extends WsBaseMessage<WsMobileGpsBody> {
  type: WsMessageType.DEVICE;
  event: WsEventType.SACON_PRODUCT_PROCESSED;
}

export interface WsSaconProductMessage extends WsBaseMessage<WsSaconProductBody> {
  type: WsMessageType.DEVICE;
  event:
    | WsEventType.SACON_PRODUCT_PROCESSED
    | WsEventType.SACON_PRODUCT_INFO
    | WsEventType.SACON_PRODUCT_ALERT
    | WsEventType.SACON_PRODUCT_WARNING
    | WsEventType.SACON_PRODUCT_ERROR;
}

export interface WsAlarmMessage extends WsBaseMessage<WsAlarmBody> {
  type: AlarmChannelMessageType;
}

export interface WsConnectMessage extends WsBaseMessage<Record<string, unknown>> {
  type: WsMessageType.CONNECT | WsMessageType.DISCONNECT;
  event: WsEventType.WS_CONNECTED | WsEventType.WS_DISCONNECTED;
}

export interface WsWorkerCountMessage extends WsBaseMessage<WsWorkerCountBody> {
  type: WsMessageType.DEVICE;
  event: WsEventType.WORKER_COUNT;
}

export interface WsDeviceCountMessage extends WsBaseMessage<WsDeviceCountBody> {
  type: WsMessageType.DEVICE;
  event: WsEventType.DEVICE_COUNT;
}

export interface WsIssuedAssetStatusMessage extends WsBaseMessage<WsIssuedAssetStatusBody> {
  type: WsMessageType.DEVICE;
  event: WsEventType.ISSUED_ASSET_STATUS;
}

export interface WsConstructionEquipmentRentalCountMessage extends WsBaseMessage<WsConstructionEquipmentRentalCountBody> {
  type: WsMessageType.DEVICE;
  event: WsEventType.CONSTRUCTION_EQUIPMENT_RENTAL_COUNT;
}

export interface WsAlarmCountChangedMessage extends WsBaseMessage<WsAlarmCountChangedBody> {
  type: WsMessageType.WIDGET;
  event: WsEventType.ALARM_COUNT_CHANGED;
}

export function isWsWorkerCountMessage(message: WsBaseMessage): message is WsWorkerCountMessage {
  return message.type === WsMessageType.DEVICE && message.event === WsEventType.WORKER_COUNT;
}

export function isWsDeviceCountMessage(message: WsBaseMessage): message is WsDeviceCountMessage {
  return message.type === WsMessageType.DEVICE && message.event === WsEventType.DEVICE_COUNT;
}

export function isWsIssuedAssetStatusMessage(message: WsBaseMessage): message is WsIssuedAssetStatusMessage {
  return message.type === WsMessageType.DEVICE && message.event === WsEventType.ISSUED_ASSET_STATUS;
}

export function isWsConstructionEquipmentRentalCountMessage(message: WsBaseMessage): message is WsConstructionEquipmentRentalCountMessage {
  return message.type === WsMessageType.DEVICE && message.event === WsEventType.CONSTRUCTION_EQUIPMENT_RENTAL_COUNT;
}

/** 알람 목록 REST 복구 신호인지 메시지 타입과 이벤트 타입을 함께 검증합니다. */
export function isWsAlarmCountChangedMessage(message: WsBaseMessage): message is WsAlarmCountChangedMessage {
  return message.type === WsMessageType.WIDGET && message.event === WsEventType.ALARM_COUNT_CHANGED;
}
